import { useGoogleFont } from './shared';

/* "Showcase" direction: image-led, minimal chrome, huge negative space --
   built specifically to make use of real generated photography (hero +
   gallery) instead of decorating around its absence like the other two
   directions have to when no imagery exists yet. Reads like a design
   studio portfolio: oversized type, spare ornamentation, the work speaks. */
const TemplateShowcase = ({ brandKit, content }) => {
  const colors = brandKit?.colors || {};
  const bg = colors.background?.hex || '#FFFFFF';
  const fg = colors.foreground?.hex || '#1A1A1A';
  const primary = colors.primary?.hex || '#1B3A5C';
  const headingFont = brandKit?.typography?.heading_font || 'Fraunces';
  const bodyFont = brandKit?.typography?.body_font || 'Inter';
  const logo = brandKit?.logos?.primary;
  const businessName = brandKit?.business_name || 'Your Business';

  useGoogleFont(headingFont);
  useGoogleFont(bodyFont);

  const services = content?.services?.length ? content.services : [];
  const gallery = content?.gallery?.filter((g) => g.image_url) || [];
  const stats = content?.stats?.filter((s) => s.value) || [];
  const heading = { fontFamily: `'${headingFont}', serif` };

  return (
    <div style={{ background: bg, color: fg, fontFamily: `'${bodyFont}', sans-serif` }} className="w-full">
      {/* Minimal nav */}
      <div className="flex items-center justify-between px-10 py-6">
        <div className="flex items-center gap-2.5">
          {logo ? (
            <img src={logo} alt={businessName} className="h-6 w-6 object-contain" />
          ) : (
            <div className="h-6 w-6" style={{ background: primary }} />
          )}
          <span style={heading} className="text-base tracking-wide">{businessName}</span>
        </div>
        <button className="text-sm underline underline-offset-4" style={{ color: primary }}>
          {content?.cta_button_text || 'Get in touch'}
        </button>
      </div>

      {/* Hero -- huge image, overlay text bottom-left. Falls back to a stark
          oversized-type block when no image exists yet. */}
      {content?.hero_image_url ? (
        <div className="relative mx-4 aspect-[16/10] overflow-hidden">
          <img src={content.hero_image_url} alt={businessName} className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.55), rgba(0,0,0,0) 55%)' }} />
          <div className="absolute bottom-0 left-0 right-0 p-10">
            <p className="text-white/80 text-xs uppercase tracking-[0.25em] mb-3">{content?.eyebrow || 'Studio'}</p>
            <h1 style={heading} className="text-white text-4xl sm:text-6xl leading-[1.05] max-w-3xl">
              {content?.headline || 'A headline that says exactly who you are.'}
            </h1>
          </div>
        </div>
      ) : (
        <div className="px-10 py-24 text-center">
          <p className="text-xs uppercase tracking-[0.25em] mb-4" style={{ color: primary }}>{content?.eyebrow || 'Studio'}</p>
          <h1 style={heading} className="text-5xl sm:text-7xl leading-[1.05] max-w-4xl mx-auto">
            {content?.headline || 'A headline that says exactly who you are.'}
          </h1>
        </div>
      )}
      <p className="text-lg opacity-70 px-10 py-10 max-w-xl">
        {content?.subheadline || 'A supporting line that explains the offer clearly.'}
      </p>

      {/* Gallery -- the primary content of this direction; falls back to a
          spare services list when no gallery exists yet */}
      {gallery.length > 0 ? (
        <div className="px-4 pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {gallery.map((g, i) => (
              <figure key={i} className={i === 0 && gallery.length % 2 !== 0 ? 'sm:col-span-2' : ''}>
                <div className={`overflow-hidden ${i === 0 ? 'aspect-[16/9]' : 'aspect-[4/3]'}`}>
                  <img src={g.image_url} alt={g.caption} className="w-full h-full object-cover" />
                </div>
                {g.caption && <figcaption className="text-xs opacity-60 pt-2">{g.caption}</figcaption>}
              </figure>
            ))}
          </div>
        </div>
      ) : (
        <div className="px-10 py-10 border-t" style={{ borderColor: `${fg}14` }}>
          {services.map((s, i) => (
            <div key={i} className="flex items-baseline justify-between py-5 border-b" style={{ borderColor: `${fg}14` }}>
              <h3 style={heading} className="text-xl">{s.title}</h3>
              <p className="opacity-60 text-sm max-w-sm text-right">{s.description}</p>
            </div>
          ))}
        </div>
      )}

      {/* Stats -- slim, oversized numbers */}
      {stats.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 px-10 py-16">
          {stats.map((s, i) => (
            <div key={i}>
              <p style={heading} className="text-4xl mb-1">{s.value}</p>
              <p className="text-xs uppercase tracking-wide opacity-60">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Services -- shown as a spare list even when gallery is present */}
      {gallery.length > 0 && services.length > 0 && (
        <div className="px-10 py-4">
          {services.map((s, i) => (
            <div key={i} className="flex items-baseline justify-between py-5 border-b" style={{ borderColor: `${fg}14` }}>
              <h3 style={heading} className="text-xl">{s.title}</h3>
              <p className="opacity-60 text-sm max-w-sm text-right">{s.description}</p>
            </div>
          ))}
        </div>
      )}

      {/* Large pull-quote testimonial */}
      <div className="px-10 py-24 text-center" style={{ background: `${primary}08` }}>
        <p style={heading} className="text-2xl sm:text-3xl italic leading-snug max-w-2xl mx-auto mb-4">
          &ldquo;{content?.testimonial_quote}&rdquo;
        </p>
        <p className="text-sm opacity-60">{content?.testimonial_author}</p>
      </div>

      {/* About */}
      <div className="px-10 py-20 max-w-2xl">
        <h2 style={heading} className="text-2xl mb-4">{content?.about_title || 'About'}</h2>
        <p className="opacity-70 leading-relaxed text-lg">{content?.about_body}</p>
      </div>

      {/* CTA -- understated, no loud color band */}
      <div className="px-10 py-20 border-t" style={{ borderColor: `${fg}14` }}>
        <h2 style={heading} className="text-3xl mb-3">{content?.cta_headline || 'Ready to start?'}</h2>
        <p className="opacity-70 mb-6 max-w-md">{content?.cta_body}</p>
        <button className="text-sm underline underline-offset-4" style={{ color: primary }}>
          {content?.cta_button_text || 'Get Started'}
        </button>
      </div>

      {/* Footer */}
      <div className="px-10 py-6 flex items-center justify-between text-xs opacity-50">
        <span>&copy; {new Date().getFullYear()} {businessName}</span>
        <span>{content?.footer_tagline}</span>
      </div>
    </div>
  );
};

export default TemplateShowcase;
